import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Volume2, VolumeX, Loader2, User, X, UserPlus, Pause, Play, Minus } from 'lucide-react';
import DTMFKeypad from './DTMFKeypad';
import { Input } from './ui/input';
import { getVoiceAudioConstraints, getHDVoiceCodecs, getNoiseFilteredStream } from '../utils/audioQuality';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPhone } from '../utils/formatPhone';
import { LogCallDialog } from './LogCallDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InboundCallManager({ dndEnabled = false }) {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, ringing, active, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState(null);
  const [audioSettings, setAudioSettings] = useState(null);
  const [activeNoiseFilter, setActiveNoiseFilter] = useState(null); // 'rnnoise' | null

  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const ringtoneCtxRef = useRef(null);       // AudioContext for synthesized ringtone
  const ringtoneIntervalRef = useRef(null);  // Interval for ring cadence
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const callDurationRef = useRef(0);          // Tracks duration without stale closure
  const handleNotificationRef = useRef(null); // Stable ref so listener always calls latest
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const localMicStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const callerInfoRef = useRef(null);         // Stable ref for phone number in upload
  const rnnoiseCleanupRef = useRef(null);     // Noise filter cleanup
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  // Dialer-bridge auto-accept: DialerPage dispatches `jtcrm-dialer-expect-bridge`
  // before calling /answer. When the resulting inbound call arrives at this
  // client we silently auto-accept (no ringtone, no popup, no log dialog).
  const expectBridgeRef = useRef(null); // { attemptId, expiresAt, leadId, phone, name, company }
  const activeBridgeRef = useRef(null); // bridge metadata for currently-active bridged call
  const bridgeRemoteAudioRef = useRef(null);
  const browserNotifRef = useRef(null);       // Active browser notification instance
  // Capture original title — never capture the ringing title (guards against HMR remount mid-flash)
  const originalTitleRef = useRef(
    document.title.includes('Incoming Call') ? 'JTCRM' : document.title
  );
  const knownSmsIdsRef = useRef(null);        // null = not yet initialized (avoids notifying on first load)
  const dndEnabledRef = useRef(dndEnabled);   // Stable ref so WS handler always reads latest DND state
  const audioSettingsRef = useRef(null);      // Stable ref so WS handler always reads latest audio prefs
  const hangupFiredRef = useRef(false);       // Guards hangup handler — Telnyx can fire hangup+destroy+purge
  const callLogIdRef = useRef(null);          // telnyx_calls record ID for current inbound call
  const callStateRef = useRef('idle');        // Stable ref so polling effect doesn't restart on state change

  const [isRecording, setIsRecording] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);


  // Log call dialog state (shown after inbound call ends)
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [logCallData, setLogCallData] = useState(null); // { phone, leadId, callerName, callerCompany }
  const [inboundCallForm, setInboundCallForm] = useState({
    phone_number: '', notes: '', call_outcome: 'connected',
    duration_seconds: 0, recording_id: null,
    schedule_followup: false, followup_date: '', followup_time: '09:00',
    mark_dnc: false, send_email_after: false, email_template_id: '',
  });
  const [savingInboundCall, setSavingInboundCall] = useState(false);

  // Quick create lead state (shown when inbound caller has no lead record)
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [createLeadForm, setCreateLeadForm] = useState({ first_name: '', last_name: '' });
  const [creatingLead, setCreatingLead] = useState(false);
  const [isActiveMinimized, setIsActiveMinimized] = useState(false);

  // Fetch audio settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const audioRes = await axios.get(`${API}/user/preferences/audio_settings`).catch(() => null);
        if (audioRes?.data?.value) {
          setAudioSettings(audioRes.data.value);
        }
      } catch (err) {
        // No settings saved, use defaults
      }
    };
    fetchSettings();

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Unlock the ringtone <audio> element on first user interaction.
    // Browsers block programmatic .play() until the user has interacted with the page.
    // This silently plays+pauses the audio at volume 0, which "unlocks" it for future plays.
    const unlockAudio = () => {
      const audioEl = document.getElementById('inbound-ringtone-audio');
      if (audioEl) {
        const origVol = audioEl.volume;
        audioEl.volume = 0;
        audioEl.play().then(() => {
          audioEl.pause();
          audioEl.currentTime = 0;
          audioEl.volume = origVol;
        }).catch(() => {});
      }
      document.removeEventListener('click', unlockAudio, true);
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('keydown', unlockAudio, true);
    };
    document.addEventListener('click', unlockAudio, true);
    document.addEventListener('touchstart', unlockAudio, true);
    document.addEventListener('keydown', unlockAudio, true);

    return () => {
      document.removeEventListener('click', unlockAudio, true);
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('keydown', unlockAudio, true);
    };
  }, []);

  // Keep refs in sync
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);
  useEffect(() => { callerInfoRef.current = callerInfo; }, [callerInfo]);
  useEffect(() => { dndEnabledRef.current = dndEnabled; }, [dndEnabled]);
  useEffect(() => { audioSettingsRef.current = audioSettings; }, [audioSettings]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Auto-stop ringtone whenever we leave 'ringing' state (belt-and-suspenders safety)
  useEffect(() => {
    if (callState !== 'ringing') {
      stopRingtone();
    }
  }, [callState]);

  // Flash page title when ringing — visible in taskbar / other tabs
  useEffect(() => {
    const safeTitle = originalTitleRef.current.includes('Incoming Call')
      ? 'JTCRM'
      : originalTitleRef.current;
    if (callState !== 'ringing') {
      document.title = safeTitle;
      return;
    }
    let show = true;
    const interval = setInterval(() => {
      document.title = show ? '\uD83D\uDCDE Incoming Call' : safeTitle;
      show = !show;
    }, 1000);
    return () => {
      clearInterval(interval);
      document.title = safeTitle;
    };
  }, [callState]);

  // Initialize WebRTC client on mount
  useEffect(() => {
    initClient();
    return () => {
      cleanup();
    };
  }, []);

  // Dialer-bridge auto-accept signal from DialerPage
  useEffect(() => {
    const onExpect = (e) => {
      expectBridgeRef.current = {
        ...(e.detail || {}),
        expiresAt: (e.detail?.expiresAt) || (Date.now() + 30000),
      };
    };
    const onHangup = () => {
      const bridged = activeBridgeRef.current;
      if (bridged?.call) {
        try { bridged.call.hangup(); } catch (err) { /* ignore */ }
      }
      activeBridgeRef.current = null;
    };
    window.addEventListener('jtcrm-dialer-expect-bridge', onExpect);
    window.addEventListener('jtcrm-dialer-bridge-hangup', onHangup);
    return () => {
      window.removeEventListener('jtcrm-dialer-expect-bridge', onExpect);
      window.removeEventListener('jtcrm-dialer-bridge-hangup', onHangup);
    };
  }, []);

  // Watchdog: if WebRTC never connected (or gave up after maxReconnectAttempts), retry every 60s
  useEffect(() => {
    if (isConnected) return;
    const watchdog = setInterval(() => {
      if (!isConnected && callState === 'idle') {
        reconnectAttempts.current = 0; // Reset so attemptReconnect works again
        initClient();
      }
    }, 60000);
    return () => clearInterval(watchdog);
  }, [isConnected, callState]);

  // Fallback polling for pending calls when WebRTC isn't connected
  useEffect(() => {
    if (isConnected) return; // WebRTC is handling it

    const pollForCalls = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/calls/pending`);
        const pendingCalls = res.data.calls || [];

        if (pendingCalls.length > 0 && callStateRef.current === 'idle' && !hangupFiredRef.current) {
          const call = pendingCalls[0];
          setCallerInfo({
            name: call.lead_name || 'Unknown Caller',
            company: call.company_name,
            phone: call.from_number,
            leadId: call.lead_id,
            callId: call.id
          });
          setCallState('ringing');
          playRingtone();
          showBrowserNotification(call.from_number);
        } else if (pendingCalls.length === 0 && callStateRef.current === 'ringing') {
          // Call disappeared (missed/answered elsewhere) — reset to idle
          setCallState('idle');
          stopRingtone();
          closeBrowserNotification();
        }
      } catch (err) {
        // Ignore polling errors
      }
    };

    // Poll every 5 seconds
    const interval = setInterval(pollForCalls, 5000);
    pollForCalls(); // Initial check

    return () => clearInterval(interval);
  }, [isConnected]); // callState read via ref — no restart on state changes

  // Ringing watchdog: detect when caller hangs up before answer.
  // Checks the actual WebRTC call object state (most reliable) + max ring timeout as fallback.
  useEffect(() => {
    if (callState !== 'ringing') return;

    const ringStartTime = Date.now();
    const MAX_RING_MS = 45000; // 45 seconds max ring before auto-stop

    const stopRingingAndNotify = () => {
      stopRingtone();
      closeBrowserNotification();
      hangupFiredRef.current = true;

      const phone = callerInfoRef.current?.phone || '';
      const callerName = callerInfoRef.current?.name || 'Unknown Caller';
      const leadId = callerInfoRef.current?.leadId || null;

      setCallState('idle');
      setIncomingCall(null);
      setCallerInfo(null);

      toast.info(`Missed call from ${callerName !== 'Unknown Caller' ? callerName : phone || 'Unknown'}`);
      dispatchNotification({
        id: `missed-${Date.now()}`,
        type: 'missed_call',
        title: `Missed call from ${callerName !== 'Unknown Caller' ? callerName : formatPhone(phone) || phone}`,
        body: formatPhone(phone) || phone,
        leadId,
        time: new Date().toISOString(),
        read: false,
      });

      const logId = callLogIdRef.current;
      callLogIdRef.current = null;
      if (logId) {
        axios.patch(`${API}/telnyx/calls/${logId}/status`, {
          status: 'missed',
          ended_at: new Date().toISOString(),
          lead_id: leadId || undefined,
          lead_name: callerName !== 'Unknown Caller' ? callerName : undefined,
        }).catch(() => {});
      }
    };

    const checkCallAlive = () => {
      if (callStateRef.current !== 'ringing') return;

      // Method 1: Check the actual WebRTC call object state
      // If the call object moved to hangup/destroy/purge/done, the caller hung up
      if (incomingCall) {
        const objState = incomingCall.state;
        if (objState && objState !== 'ringing' && objState !== 'new' && objState !== 'requesting' && objState !== 'trying' && objState !== 'early') {
          stopRingingAndNotify();
          return;
        }
      }

      // Method 2: Max ring timeout — phone typically rings 30-45s before voicemail
      if (Date.now() - ringStartTime > MAX_RING_MS) {
        stopRingingAndNotify();
        return;
      }
    };

    // Check frequently — call object state updates are immediate
    const interval = setInterval(checkCallAlive, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [callState, incomingCall]); // Re-runs when callState or incomingCall changes

  const initClient = async () => {
    try {
      // Use cached credentials from sessionStorage to speed up reconnect after page reload
      // (credentials don't change between sessions; cache expires with the tab)
      let username, password;
      const cached = sessionStorage.getItem('telnyx_webrtc_creds');
      if (cached) {
        try { ({ username, password } = JSON.parse(cached)); } catch (e) { /* ignore */ }
      }

      if (!username || !password) {
        // Check if Telnyx is configured for this user
        const statusRes = await axios.get(`${API}/telnyx/status`);
        if (!statusRes.data?.webrtc_enabled) return;

        // Fetch and cache credentials
        const credRes = await axios.get(`${API}/telnyx/webrtc-credentials`);
        ({ username, password } = credRes.data);
        if (!username || !password) return;
        sessionStorage.setItem('telnyx_webrtc_creds', JSON.stringify({ username, password }));
      }

      const telnyxClient = new TelnyxRTC({
        login: username,
        password: password,
        // Use our custom WAV ringtone — SDK handles autoplay and plays on incoming calls
        ringtoneFile: '/ringtone.wav',
        // Disable ringback (outbound ring-back handled by TelnyxWebRTCCall)
        ringbackFile: '',
        prefetchIceCandidates: true,
      });

      telnyxClient.on('telnyx.ready', () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      });

      telnyxClient.on('telnyx.error', (error) => {
        console.error('Inbound call manager error:', error);
        setIsConnected(false);
        attemptReconnect();
      });

      telnyxClient.on('telnyx.socket.close', () => {
        setIsConnected(false);
        attemptReconnect();
      });

      telnyxClient.on('telnyx.notification', (notification) => {
        if (handleNotificationRef.current) handleNotificationRef.current(notification);
      });

      await telnyxClient.connect();
      setClient(telnyxClient);
      
    } catch (err) {
      console.error('Failed to init inbound call manager:', err);
      sessionStorage.removeItem('telnyx_webrtc_creds'); // Clear cache on error — creds may have changed
      attemptReconnect();
    }
  };

  const attemptReconnect = () => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      return;
    }

    reconnectAttempts.current++;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
    
    setTimeout(() => {
      initClient();
    }, delay);
  };

  const cleanup = () => {
    if (client) {
      try {
        client.disconnect();
      } catch (e) {
        // ignore disconnect errors on cleanup
      }
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
    }
  };

  const handleNotification = useCallback(async (notification) => {
    const call = notification.call;
    if (!call) return;


    // InboundCallManager never makes outbound calls, so accept all call updates.
    // Direction may be 'inbound', undefined, or change during hangup — don't filter by it.
    if (notification.type === 'callUpdate') {
      const state = call.state;

      // Trigger auto-accept on the FIRST pre-active state, not just ringing/new.
      // Telnyx sometimes fires `early`/`requesting`/`trying` first; if we wait
      // for `ringing` the popup setup path can race ahead.
      const isPreAnswer = state === 'ringing' || state === 'new'
        || state === 'early' || state === 'requesting' || state === 'trying';

      if (isPreAnswer) {
        // Dialer-bridge auto-accept: if DialerPage just signaled it expects a
        // bridged call, silently answer this one — no ringtone, no popup, no log
        // dialog. The DialerPage owns the in-call UI for this scenario.
        const expect = expectBridgeRef.current;
        if (expect && expect.expiresAt > Date.now() && !activeBridgeRef.current) {
          expectBridgeRef.current = null;
          activeBridgeRef.current = { ...expect, call };
          try {
            const audioConstraints = getVoiceAudioConstraints(audioSettings?.microphone_id);
            const preferred_codecs = getHDVoiceCodecs();
            const rnnoiseEnabled = audioSettings?.rnnoise_enabled !== false;
            const filtered = await getNoiseFilteredStream(audioSettings?.microphone_id, rnnoiseEnabled);
            const answerOptions = { video: false, useStereo: false, ...(preferred_codecs ? { preferred_codecs } : {}) };
            if (filtered) {
              answerOptions.localStream = filtered.stream;
              rnnoiseCleanupRef.current = filtered.cleanup;
            } else {
              answerOptions.audio = audioConstraints;
            }
            await call.answer(answerOptions);
            const tryAttachAudio = (attempt) => {
              const stream = call.remoteStream;
              if (stream && bridgeRemoteAudioRef.current) {
                bridgeRemoteAudioRef.current.srcObject = stream;
                bridgeRemoteAudioRef.current.play().catch(() => {});
                if (audioSettings?.speaker_id && bridgeRemoteAudioRef.current.setSinkId) {
                  bridgeRemoteAudioRef.current.setSinkId(audioSettings.speaker_id).catch(() => {});
                }
              } else if (attempt < 12) {
                setTimeout(() => tryAttachAudio(attempt + 1), 250);
              }
            };
            tryAttachAudio(0);
            window.dispatchEvent(new CustomEvent('jtcrm-dialer-bridge-active', {
              detail: { attemptId: expect.attemptId, call },
            }));
          } catch (err) {
            console.error('[DialerBridge] auto-accept failed:', err);
            activeBridgeRef.current = null;
            window.dispatchEvent(new CustomEvent('jtcrm-dialer-bridge-failed', {
              detail: { attemptId: expect.attemptId, error: String(err?.message || err) },
            }));
          }
          return;
        }

        // Popup setup only fires on `ringing`/`new` — earlier states
        // (`early`/`requesting`/`trying`) just give the auto-accept path a
        // chance to run before the popup machinery starts.
        if (state === 'ringing' || state === 'new') {
          hangupFiredRef.current = false; // Reset so the next hangup is processed
          setIncomingCall(call);
          setCallState('ringing');

          // Try to identify caller
          const fromNumber = call.options?.remoteCallerNumber || call.options?.callerNumber || 'Unknown';
          lookupCaller(fromNumber);

          playRingtone();
          showBrowserNotification(fromNumber);

          // Log this inbound call to the backend (Telnyx doesn't fire call.initiated webhooks for WebRTC)
          if (!callLogIdRef.current) {
            const logId = `inbound-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
            callLogIdRef.current = logId;
            axios.post(`${API}/telnyx/calls/log-inbound`, {
              call_id: logId,
              from_number: fromNumber,
            }).then(res => {
              // Backend may return a different id (upsert), keep in sync
              if (res.data?.id) callLogIdRef.current = res.data.id;
            }).catch(() => {}); // Non-critical — don't block UI
          }
        }
      } else if (state === 'active') {
        // If this is the dialer-bridge call, skip the inbound-call UI takeover —
        // audio is already wired to the bridge audio element.
        if (activeBridgeRef.current?.call === call) {
          return;
        }
        // Call was answered — answerCall() already set state, but this also fires for
        // remote answer or if answerCall's await resolves after notification
        setActiveCall(call);
        setCallState('active');
        stopRingtone();
        if (!callStartTimeRef.current) {
          callStartTimeRef.current = Date.now();
          startDurationTimer();
        }

        // Attach remote audio + start recording — poll for stream (may not be ready immediately)
        const tryAttachAudio = (attempt) => {
          const stream = call.remoteStream;
          if (stream && remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream;
            remoteAudioRef.current.play().catch(() => {});
            const speakerId = audioSettings?.speaker_id;
            if (speakerId && remoteAudioRef.current.setSinkId) {
              remoteAudioRef.current.setSinkId(speakerId)
                .catch(() => {});
            }
            startRecording(stream);
          } else if (attempt < 12) {
            setTimeout(() => tryAttachAudio(attempt + 1), 250);
          }
        };
        tryAttachAudio(0);

        toast.success('Call connected');
      } else if (state === 'hangup' || state === 'destroy' || state === 'purge') {
        // Dialer-bridge call ended — tell DialerPage and skip the inbound-call cleanup path
        if (activeBridgeRef.current?.call === call) {
          const meta = activeBridgeRef.current;
          activeBridgeRef.current = null;
          if (bridgeRemoteAudioRef.current) {
            try {
              bridgeRemoteAudioRef.current.pause();
              bridgeRemoteAudioRef.current.srcObject = null;
            } catch (e) { /* ignore */ }
          }
          if (rnnoiseCleanupRef.current) {
            try { rnnoiseCleanupRef.current(); } catch (e) { /* ignore */ }
            rnnoiseCleanupRef.current = null;
          }
          window.dispatchEvent(new CustomEvent('jtcrm-dialer-bridge-ended', {
            detail: { attemptId: meta.attemptId },
          }));
          return;
        }
        // Guard: Telnyx fires hangup + destroy + purge in sequence — only process once
        if (hangupFiredRef.current) return;
        hangupFiredRef.current = true;
        stopRingtone();
        stopDurationTimer();
        const dur = callDurationRef.current;
        const wasAnswered = !!callStartTimeRef.current;

        // Capture caller info BEFORE clearing state
        const phone = callerInfoRef.current?.phone || '';
        const leadId = callerInfoRef.current?.leadId || null;
        const callerName = callerInfoRef.current?.name || 'Unknown Caller';
        const callerCompany = callerInfoRef.current?.company || '';

        // Clear call UI immediately
        callStartTimeRef.current = null;
        setCallState('idle');
        setIncomingCall(null);
        setActiveCall(null);
        setCallerInfo(null);
        setIsOnHold(false);

        closeBrowserNotification();

        const logId = callLogIdRef.current;
        callLogIdRef.current = null; // Reset for next call

        if (wasAnswered) {
          toast.info(`Call ended - ${formatDuration(dur)}`);
          // Update call log status to answered
          if (logId) {
            axios.patch(`${API}/telnyx/calls/${logId}/status`, {
              status: 'answered',
              answered_at: new Date().toISOString(),
              duration_secs: dur,
              lead_id: leadId || undefined,
              lead_name: callerName !== 'Unknown Caller' ? callerName : undefined,
            }).catch(() => {});
          }
          stopAndUpload(dur).then((recordingId) => {
            // Link recording to the call log
            if (logId && recordingId) {
              axios.patch(`${API}/telnyx/calls/${logId}/status`, {
                status: 'completed',
                recording_id: recordingId,
                duration_secs: dur,
                ended_at: new Date().toISOString(),
              }).catch(() => {});
            }
            setLogCallData({ phone, leadId, callerName, callerCompany });
            setInboundCallForm({
              phone_number: phone,
              notes: '',
              call_outcome: dur > 0 && dur < 8 ? 'voicemail' : 'connected',
              duration_seconds: dur,
              recording_id: recordingId || null,
              schedule_followup: false,
              followup_date: '',
              followup_time: '09:00',
              mark_dnc: false,
              send_email_after: false,
              email_template_id: '',
            });
            if (leadId) {
              setShowLogDialog(true);
            } else {
              setCreateLeadForm({ first_name: callerName !== 'Unknown Caller' ? callerName : '', last_name: '' });
              setShowCreateLead(true);
            }
          });
        } else {
          // Missed call — caller hung up before being answered
          toast.info(`Missed call from ${phone || 'Unknown'}`);
          showMissedCallNotification(phone, callerName !== 'Unknown Caller' ? callerName : null);
          dispatchNotification({
            id: `missed-${Date.now()}`,
            type: 'missed_call',
            title: `Missed call from ${callerName !== 'Unknown Caller' ? callerName : formatPhone(phone) || phone}`,
            body: formatPhone(phone) || phone,
            leadId: leadId || null,
            time: new Date().toISOString(),
            read: false,
          });
          // Update call log status to missed
          if (logId) {
            axios.patch(`${API}/telnyx/calls/${logId}/status`, {
              status: 'missed',
              ended_at: new Date().toISOString(),
              lead_id: leadId || undefined,
              lead_name: callerName !== 'Unknown Caller' ? callerName : undefined,
            }).catch(() => {});
          }
        }
      }
    }
  }, [audioSettings]);

  // Keep ref current so the listener (bound once at init) always calls the latest version
  useEffect(() => {
    handleNotificationRef.current = handleNotification;
  }, [handleNotification]);

  const lookupCaller = async (phoneNumber) => {
    try {
      const cleanNumber = phoneNumber.replace(/\D/g, '');
      const searchQuery = cleanNumber.slice(-10);
      const res = await axios.get(`${API}/leads?search=${searchQuery}&limit=1`);
      if (res.data.items?.length > 0) {
        const lead = res.data.items[0];
        const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown';
        setCallerInfo({
          name,
          company: lead.company_name,
          phone: phoneNumber,
          leadId: lead.id
        });
        // Update notification now that we have the caller's name
        showBrowserNotification(phoneNumber, name, lead.company_name);
        // Patch call log with lead info as soon as it's resolved
        if (callLogIdRef.current) {
          axios.patch(`${API}/telnyx/calls/${callLogIdRef.current}/status`, {
            lead_id: lead.id,
            lead_name: name,
          }).catch(() => {});
        }
      } else {
        setCallerInfo({ name: 'Unknown Caller', phone: phoneNumber });
      }
    } catch (err) {
      console.error('Caller lookup failed:', err);
      setCallerInfo({ name: 'Unknown Caller', phone: phoneNumber });
    }
  };

  const playRingtone = () => {
    // The Telnyx SDK plays /ringtone.wav automatically via ringtoneFile config.
    // This function is a backup — if DND or sound disabled, silence everything.
    if (dndEnabled || audioSettings?.call_sound_enabled === false) {
      stopRingtone();
      return;
    }
    // Don't call stopRingtone() here — it would kill the SDK's ringtone audio!
    // Just ensure our DOM audio is also playing as a fallback (in case SDK doesn't play)
    try {
      const audioEl = document.getElementById('inbound-ringtone-audio');
      if (audioEl && audioEl.paused) {
        audioEl.currentTime = 0;
        audioEl.play().catch(() => {});
        ringtoneRef.current = audioEl;
      }
    } catch (e) {}
  };

  const stopRingtone = () => {
    // Stop DOM <audio> element
    try {
      const audioEl = document.getElementById('inbound-ringtone-audio');
      if (audioEl) {
        audioEl.pause();
        audioEl.currentTime = 0;
      }
    } catch (e) {}
    // Stop any standalone Audio ref (fallback path)
    if (ringtoneRef.current) {
      try {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      } catch (e) {}
      ringtoneRef.current = null;
    }
    // Stop synthesized ringtone
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    if (ringtoneCtxRef.current) {
      try { ringtoneCtxRef.current.close(); } catch (e) {}
      ringtoneCtxRef.current = null;
    }
    // Nuclear: find and pause ANY playing <audio> elements on the page
    // (catches Telnyx SDK built-in ringtone elements we don't control)
    try {
      document.querySelectorAll('audio').forEach((el) => {
        // Don't pause the remote call audio (it carries the actual voice)
        if (el === remoteAudioRef.current) return;
        if (!el.paused) {
          el.pause();
          el.currentTime = 0;
        }
      });
    } catch (e) {}
  };

  const dispatchNotification = (detail) => {
    window.dispatchEvent(new CustomEvent('crm-notification', { detail }));
  };

  const closeBrowserNotification = () => {
    if (browserNotifRef.current) {
      try { browserNotifRef.current.close(); } catch (e) {}
      browserNotifRef.current = null;
    }
  };

  const showBrowserNotification = (phoneNumber, callerName = null, callerCompany = null) => {
    if (dndEnabled) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    closeBrowserNotification();
    const displayName = callerName || formatPhone(phoneNumber) || phoneNumber;
    const body = callerCompany ? `${displayName}\n${callerCompany}` : displayName;
    const notif = new Notification('\uD83D\uDCDE Incoming Call', {
      body,
      icon: '/logo192.png',
      requireInteraction: true,
      tag: 'incoming-call',
    });
    notif.onclick = () => {
      window.focus();
      notif.close();
      browserNotifRef.current = null;
    };
    browserNotifRef.current = notif;
  };

  const showMissedCallNotification = (phoneNumber, callerName = null) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    closeBrowserNotification();
    const displayName = callerName || formatPhone(phoneNumber) || phoneNumber;
    const notif = new Notification('Missed Call', {
      body: displayName,
      icon: '/logo192.png',
      tag: 'incoming-call',
    });
    notif.onclick = () => {
      window.focus();
      notif.close();
      browserNotifRef.current = null;
    };
    browserNotifRef.current = notif;
  };

  // Play a short notification ding using Web Audio API (no audio file needed)
  const playNotificationSound = (type = 'sms') => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const gainNode = audioCtx.createGain();
      gainNode.connect(audioCtx.destination);

      const freqs = type === 'call'
        ? [880, 1100, 880] // double-ring pattern for calls
        : [1100, 880];     // descending ding for SMS

      let offset = 0;
      freqs.forEach((freq) => {
        const osc = audioCtx.createOscillator();
        osc.connect(gainNode);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gainNode.gain.setValueAtTime(0.25, audioCtx.currentTime + offset);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + offset + 0.35);
        osc.start(audioCtx.currentTime + offset);
        osc.stop(audioCtx.currentTime + offset + 0.35);
        offset += 0.4;
      });
    } catch (e) {
      // Web Audio not available
    }
  };

  // Real-time SMS notifications via WebSocket, with 30s poll fallback
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let pollTimer = null;
    let backoff = 1000; // ms — doubles each attempt, capped at 30s
    let isDestroyed = false;
    let wsConnected = false;

    // Process confirmed-new messages: play sound, OS notif (tab-hidden only), bell panel
    const processNewSms = (newMessages) => {
      if (!Array.isArray(newMessages) || newMessages.length === 0) return;

      // Track IDs so poll doesn't re-fire what WS already handled
      if (!knownSmsIdsRef.current) knownSmsIdsRef.current = new Set();
      newMessages.forEach(m => knownSmsIdsRef.current.add(m.id));

      if (!dndEnabledRef.current && audioSettingsRef.current?.sms_sound_enabled !== false) {
        playNotificationSound('sms');
      }

      // Only show OS notification when tab is hidden — prevents double-notification when focused
      if (!dndEnabledRef.current && document.hidden &&
          'Notification' in window && Notification.permission === 'granted') {
        const bySender = {};
        newMessages.forEach(m => {
          const key = m.from_number || 'unknown';
          if (!bySender[key]) bySender[key] = { name: m.lead_name || formatPhone(m.from_number) || m.from_number || 'Unknown', msgs: [] };
          bySender[key].msgs.push(m);
        });
        const senderGroups = Object.values(bySender);
        if (senderGroups.length === 1) {
          const { name, msgs } = senderGroups[0];
          const title = msgs.length > 1 ? `${msgs.length} new SMS from ${name}` : `New SMS from ${name}`;
          const preview = msgs.length > 1 ? `${msgs.length} messages` : (msgs[0].body || '').substring(0, 80);
          const notif = new Notification(title, { body: preview, icon: '/logo192.png', tag: `sms-${msgs[0].from_number}` });
          notif.onclick = () => { window.focus(); notif.close(); };
        } else {
          const total = newMessages.length;
          const names = senderGroups.slice(0, 3).map(g => g.name).join(', ');
          const notif = new Notification(`${total} new SMS messages`, { body: names, icon: '/logo192.png', tag: 'sms-multi' });
          notif.onclick = () => { window.focus(); notif.close(); };
        }
      }

      // Dispatch to notification bell history panel (one entry per sender group)
      const grouped = {};
      newMessages.forEach(m => {
        const k = m.from_number || 'unknown';
        if (!grouped[k]) grouped[k] = { name: m.lead_name || formatPhone(m.from_number) || m.from_number || 'Unknown', msgs: [], leadId: m.lead_id || null, phone: m.from_number };
        grouped[k].msgs.push(m);
      });
      Object.values(grouped).forEach(({ name, msgs, leadId, phone }) => {
        dispatchNotification({
          id: `sms-${phone}-${Date.now()}`,
          type: 'sms',
          title: msgs.length > 1 ? `${msgs.length} new SMS from ${name}` : `New SMS from ${name}`,
          body: msgs.length > 1 ? `${msgs.length} messages` : (msgs[0].body || '').substring(0, 80),
          leadId,
          time: new Date().toISOString(),
          read: false,
        });
      });
    };

    // Poll fallback (runs only when WS is down)
    const poll = async () => {
      if (wsConnected) return;
      try {
        const res = await axios.get(`${API}/telnyx/sms/inbound`);
        const messages = res.data.messages || [];
        if (!knownSmsIdsRef.current) {
          knownSmsIdsRef.current = new Set(messages.map(m => m.id));
          return;
        }
        const newMessages = messages.filter(m => !knownSmsIdsRef.current.has(m.id));
        if (newMessages.length > 0) processNewSms(newMessages);
        // Merge all current IDs so nothing fires again
        messages.forEach(m => knownSmsIdsRef.current.add(m.id));
      } catch (e) { /* ignore */ }
    };

    const connectWs = (wsToken) => {
      if (isDestroyed) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const tokenParam = wsToken ? `?token=${encodeURIComponent(wsToken)}` : '';
      const wsUrl = backendUrl.replace(/^http/, 'ws') + '/ws/notifications' + tokenParam;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        wsConnected = true;
        backoff = 1000; // Reset on successful connect
        clearInterval(pollTimer);
        pollTimer = null;
        // Keepalive ping every 30s
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_sms' && data.message) {
            if (!knownSmsIdsRef.current) knownSmsIdsRef.current = new Set();
            // Only notify if genuinely new (guard against duplicates)
            if (!knownSmsIdsRef.current.has(data.message.id)) {
              processNewSms([data.message]);
            }
          }
        } catch (e) { /* invalid JSON */ }
      };

      ws.onclose = () => {
        wsConnected = false;
        clearInterval(pingTimer);
        pingTimer = null;
        if (!isDestroyed) {
          // Fall back to polling while reconnecting
          if (!pollTimer) {
            poll();
            pollTimer = setInterval(poll, 30000);
          }
          reconnectTimer = setTimeout(() => {
            backoff = Math.min(backoff * 2, 30000);
            connectWs(wsToken);
          }, backoff);
        }
      };

      ws.onerror = () => { ws.close(); };
    };

    // Seed known IDs first (so we don't replay existing messages), then open WS
    let wsToken = null;
    const init = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/sms/inbound`);
        knownSmsIdsRef.current = new Set((res.data.messages || []).map(m => m.id));
      } catch (e) {
        knownSmsIdsRef.current = new Set();
      }
      // Get a WS auth token (needed when SameSite=Lax blocks cookie on cross-port WS)
      try {
        const tokenRes = await axios.get(`${API}/auth/ws-token`);
        wsToken = tokenRes.data.token;
      } catch (e) { /* fall back to cookie auth */ }
      if (!isDestroyed) connectWs(wsToken);
    };

    init();

    return () => {
      isDestroyed = true;
      clearInterval(pollTimer);
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const answerCall = async () => {
    if (!incomingCall) return;
    closeBrowserNotification();

    try {
      const audioConstraints = getVoiceAudioConstraints(audioSettings?.microphone_id);
      const preferred_codecs = getHDVoiceCodecs();

      // Set active state BEFORE awaiting answer — prevents UI gap
      // (ringing UI gone because incomingCall cleared, active UI not shown because callState still 'ringing')
      setCallState('active');
      setActiveCall(incomingCall);
      setIncomingCall(null);
      stopRingtone();
      callStartTimeRef.current = Date.now();
      startDurationTimer();

      const rnnoiseEnabled = audioSettings?.rnnoise_enabled !== false;
      const filtered = await getNoiseFilteredStream(audioSettings?.microphone_id, rnnoiseEnabled);

      const answerOptions = {
        video: false,
        useStereo: false,
        ...(preferred_codecs ? { preferred_codecs } : {}),
      };

      if (filtered) {
        answerOptions.localStream = filtered.stream;
        rnnoiseCleanupRef.current = filtered.cleanup;
        setActiveNoiseFilter(filtered.filterType);
      } else {
        answerOptions.audio = audioConstraints;
        setActiveNoiseFilter(null);
      }

      await incomingCall.answer(answerOptions);

      // Attach remote audio after answer resolves (stream may be ready now)
      const tryAttachAudio = (attempt) => {
        const stream = incomingCall.remoteStream;
        if (stream && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.play().catch(() => {});
          if (audioSettings?.speaker_id && remoteAudioRef.current.setSinkId) {
            remoteAudioRef.current.setSinkId(audioSettings.speaker_id).catch(() => {});
          }
          startRecording(stream);
        } else if (attempt < 12) {
          setTimeout(() => tryAttachAudio(attempt + 1), 250);
        }
      };
      tryAttachAudio(0);

      // Enable server-side noise suppression for cleaner audio to the remote party
      const callControlId = incomingCall.telnyxCallControlId;
      if (callControlId) {
        axios.post(`${API}/telnyx/noise-suppression`, {
          call_control_id: callControlId,
          direction: 'both',
        }).catch(e => console.error('Noise suppression not available:', e.message));
      }

      // Navigate to lead page if we know who's calling
      const leadId = callerInfoRef.current?.leadId;
      if (leadId) {
        navigate(`/admin/leads/${leadId}`);
      }

    } catch (err) {
      console.error('Failed to answer call:', err);
      toast.error('Failed to answer call');
      // Revert state on failure
      setCallState('idle');
      setActiveCall(null);
      stopDurationTimer();
      callStartTimeRef.current = null;
    }
  };

  const declineCall = () => {
    closeBrowserNotification();
    if (incomingCall) {
      try {
        // Use reject() (SIP 603 Decline) to terminate the call cleanly.
        // hangup() (SIP BYE) would route to voicemail, but if voicemail isn't configured
        // on Telnyx, it shows "connected" in limbo. reject() ends it for both sides.
        if (typeof incomingCall.reject === 'function') {
          incomingCall.reject();
        } else {
          incomingCall.hangup();
        }
      } catch (e) {
        // ignore
      }
    }
    stopRingtone();
    setIncomingCall(null);
    setCallState('idle');
  };

  const sendToVoicemail = () => {
    // SIP BYE during ring — Telnyx routes to the connection's configured voicemail
    // box. If no voicemail is configured, behaves the same as decline for the caller.
    closeBrowserNotification();
    if (incomingCall) {
      try {
        incomingCall.hangup();
      } catch (e) {
        // ignore
      }
    }
    stopRingtone();
    setIncomingCall(null);
    setCallState('idle');
    toast.info('Sent to voicemail');
  };

  const hangUp = () => {
    if (activeCall) {
      try {
        activeCall.hangup();
      } catch (e) {
        // ignore
      }
    }
    // Recording stop+upload handled by hangup state in handleNotification.
    // Stop recorder now in case state event doesn't fire.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    // Clean up RNNoise filter
    if (rnnoiseCleanupRef.current) {
      rnnoiseCleanupRef.current();
      rnnoiseCleanupRef.current = null;
    }
    stopDurationTimer();
    setActiveCall(null);
    setCallState('idle');
    setCallerInfo(null);
  };

  const toggleMute = () => {
    if (activeCall) {
      if (isMuted) {
        activeCall.unmuteAudio();
      } else {
        activeCall.muteAudio();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleDeafen = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !isDeafened;
      setIsDeafened(!isDeafened);
    }
  };

  const toggleHold = () => {
    if (!activeCall) return;
    try {
      if (isOnHold) {
        activeCall.unhold();
        setIsOnHold(false);
        toast.info('Call resumed');
      } else {
        activeCall.hold();
        setIsOnHold(true);
        toast.info('Call on hold');
      }
    } catch (e) {
      toast.error('Hold not supported on this call');
    }
  };



  const startRecording = async (remoteStream) => {
    if (!remoteStream || !window.MediaRecorder) return;
    try {
      recordingChunksRef.current = [];
      let streamToRecord = remoteStream;

      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        localMicStreamRef.current = localStream;

        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const remoteSource = audioCtx.createMediaStreamSource(remoteStream);
        const localSource = audioCtx.createMediaStreamSource(localStream);
        const destination = audioCtx.createMediaStreamDestination();
        remoteSource.connect(destination);
        localSource.connect(destination);
        audioCtxRef.current = audioCtx;
        streamToRecord = destination.stream;
      } catch (e) {
        // Could not get local mic — record remote only
      }

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(streamToRecord, mimeType ? { mimeType } : {});
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (e) {
      console.warn('Could not start inbound recording:', e);
    }
  };

  const stopAndUpload = async (duration) => {
    const recorder = mediaRecorderRef.current;
    setIsRecording(false);

    if (localMicStreamRef.current) {
      localMicStreamRef.current.getTracks().forEach(t => t.stop());
      localMicStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }

    if (!recorder || recorder.state === 'inactive') return;

    try {
      const blob = await new Promise((resolve) => {
        recorder.onstop = () => {
          const type = recorder.mimeType || 'audio/webm';
          resolve(new Blob(recordingChunksRef.current, { type }));
        };
        recorder.stop();
      });

      if (blob.size < 500) return;

      const phone = callerInfoRef.current?.phone || '';
      const leadId = callerInfoRef.current?.leadId;
      const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('file', blob, `inbound-recording.${ext}`);
      formData.append('phone_number', phone);
      formData.append('duration_secs', String(duration));
      formData.append('direction', 'inbound');
      if (leadId) formData.append('lead_id', leadId);

      const res = await axios.post(`${API}/telnyx/recordings/webrtc-upload`, formData, { timeout: 30000 });
      return res.data.recording_id || null;
    } catch (e) {
      console.warn('Inbound recording upload failed:', e);
      return null;
    }
  };

  const handleInboundCallSubmit = async () => {
    if (!logCallData?.leadId) {
      toast.error('No lead found for this caller — cannot log call');
      return;
    }
    setSavingInboundCall(true);
    try {
      await axios.post(`${API}/communications/call`, {
        lead_id: logCallData.leadId,
        phone_number: inboundCallForm.phone_number,
        notes: inboundCallForm.notes,
        call_outcome: inboundCallForm.call_outcome,
        duration_seconds: inboundCallForm.duration_seconds,
        recording_id: inboundCallForm.recording_id || undefined,
        direction: 'inbound',
      });

      if (inboundCallForm.schedule_followup) {
        let dueDate;
        if (inboundCallForm.followup_date) {
          dueDate = new Date(inboundCallForm.followup_date + 'T' + (inboundCallForm.followup_time || '09:00'));
        } else {
          dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 1);
          dueDate.setHours(10, 0, 0, 0);
        }
        await axios.post(`${API}/tasks`, {
          lead_id: logCallData.leadId,
          description: `Follow-up call with ${logCallData.callerName}`,
          due_date: dueDate.toISOString(),
          task_type: 'call',
        });
      }

      if (inboundCallForm.mark_dnc) {
        await axios.put(`${API}/leads/${logCallData.leadId}`, { dnc: true });
        toast.success('Call logged & lead marked Do Not Call');
      } else if (inboundCallForm.schedule_followup) {
        toast.success('Call logged & follow-up task created');
      } else {
        toast.success('Call logged');
      }

      setShowLogDialog(false);
      setLogCallData(null);
    } catch (err) {
      toast.error('Failed to log call');
    } finally {
      setSavingInboundCall(false);
    }
  };

  const handleQuickCreateLead = async () => {
    if (!createLeadForm.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setCreatingLead(true);
    try {
      const phone = logCallData?.phone || '';
      const res = await axios.post(`${API}/leads`, {
        first_name: createLeadForm.first_name.trim(),
        last_name: createLeadForm.last_name.trim(),
        mobile_phone: phone,
        lead_type: 'crm',
      });
      const newLeadId = res.data?.id;
      if (!newLeadId) throw new Error('No ID returned');

      // Update logCallData with the new lead's ID, then show log dialog
      setLogCallData(prev => ({ ...prev, leadId: newLeadId, callerName: `${createLeadForm.first_name} ${createLeadForm.last_name}`.trim() }));
      setInboundCallForm(prev => ({ ...prev, phone_number: phone }));
      setShowCreateLead(false);
      setShowLogDialog(true);
      toast.success('Lead created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setCreatingLead(false);
    }
  };

  const startDurationTimer = () => {
    durationIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
        setCallDuration(elapsed);
      }
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    setCallDuration(0);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // When idle, only render audio + post-call dialogs
  if (callState === 'idle') {
    return (
      <>
        <audio ref={remoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />
        <audio ref={bridgeRemoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />

        {/* Quick create lead — shown when caller has no lead record */}
        {showCreateLead && logCallData && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
            <Card className="w-96 shadow-2xl">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <UserPlus className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold">New Caller</p>
                    <p className="text-sm text-slate-500">{formatPhone(logCallData.phone)} — not in your leads</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>First Name *</Label>
                    <Input
                      value={createLeadForm.first_name}
                      onChange={e => setCreateLeadForm(f => ({ ...f, first_name: e.target.value }))}
                      placeholder="First name"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Last Name</Label>
                    <Input
                      value={createLeadForm.last_name}
                      onChange={e => setCreateLeadForm(f => ({ ...f, last_name: e.target.value }))}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Phone {formatPhone(logCallData.phone)} will be saved to the new lead.</p>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowCreateLead(false);
                      // Skip creating — go straight to log dialog (submit will fail without leadId, user can dismiss)
                      setShowLogDialog(true);
                    }}
                  >
                    Skip
                  </Button>
                  <Button
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleQuickCreateLead}
                    disabled={creatingLead || !createLeadForm.first_name.trim()}
                  >
                    {creatingLead ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                    Create & Log Call
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Log call dialog */}
        {showLogDialog && logCallData && (
          <LogCallDialog
            open={showLogDialog}
            onClose={() => { setShowLogDialog(false); setLogCallData(null); }}
            lead={{
              first_name: logCallData.callerName,
              last_name: '',
              company_name: logCallData.callerCompany,
              phone: logCallData.phone,
              phone_number: logCallData.phone,
            }}
            callForm={inboundCallForm}
            setCallForm={setInboundCallForm}
            onSubmit={handleInboundCallSubmit}
            saving={savingInboundCall}
            templates={[]}
            leadId={logCallData.leadId}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Remote audio element — off-screen so Chrome allows autoplay */}
      <audio ref={remoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />
      <audio ref={bridgeRemoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />
      {/* Ringtone audio element — DOM-rendered for reliable stop/start */}
      <audio id="inbound-ringtone-audio" src="/ringtone.wav" loop preload="auto" style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />

      {/* Incoming Call Notification — Centered panel */}
      {callState === 'ringing' && incomingCall && (
        <>
          <div className="fixed inset-0 z-[99] bg-black/40" />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-sm"
            style={{ animation: 'dialerScaleIn 0.15s ease-out' }}
            data-testid="incoming-call-notification"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                  <PhoneIncoming className="h-8 w-8 text-white" />
                </div>
                <p className="text-white font-bold text-lg">Incoming Call</p>
                <p className="text-white/80 text-xs mt-0.5">Ringing...</p>
              </div>

              {/* Caller Info */}
              <div className="px-5 pt-5 pb-2 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="h-7 w-7 text-slate-500" />
                </div>
                <p className="font-bold text-slate-900 text-lg">
                  {callerInfo?.name || 'Unknown Caller'}
                </p>
                {callerInfo?.company && (
                  <p className="text-sm text-slate-500 mt-0.5">{callerInfo.company}</p>
                )}
                <p className="text-base font-mono text-slate-600 mt-1">
                  {formatPhone(callerInfo?.phone)}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="px-5 pb-5 pt-3 space-y-2">
                <Button
                  onClick={answerCall}
                  className="w-full h-12 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  data-testid="answer-call-btn"
                >
                  <Phone className="h-5 w-5" />
                  Answer
                </Button>
                <div className="flex gap-2">
                  <Button
                    onClick={sendToVoicemail}
                    variant="outline"
                    className="flex-1 h-11 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl"
                    data-testid="send-to-vm-btn"
                  >
                    <Volume2 className="h-4 w-4" />
                    Send to VM
                  </Button>
                  <Button
                    onClick={declineCall}
                    className="flex-1 h-11 gap-2 bg-red-500 hover:bg-red-600 text-white rounded-xl"
                    data-testid="decline-call-btn"
                  >
                    <PhoneOff className="h-4 w-4" />
                    Decline
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Active Call Widget — Minimized pill */}
      {callState === 'active' && activeCall && isActiveMinimized && (
        <button
          onClick={() => setIsActiveMinimized(false)}
          className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full pl-3 pr-4 py-3 shadow-lg transition-all duration-200 hover:scale-105"
          data-testid="active-call-minimized"
        >
          <div className="relative">
            <Phone className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full" />
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold leading-tight">{callerInfo?.name || 'Active Call'}</p>
            <p className="text-xs font-mono opacity-80">{formatDuration(callDuration)}</p>
          </div>
        </button>
      )}

      {/* Active Call Widget — Expanded centered panel */}
      {callState === 'active' && activeCall && !isActiveMinimized && (
        <>
          <div className="fixed inset-0 z-[99] bg-black/40" />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-sm"
            style={{ animation: 'dialerScaleIn 0.15s ease-out' }}
            data-testid="active-call-widget"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">{callerInfo?.name || 'Active Call'}</p>
                    <p className="text-2xl font-mono font-bold opacity-95">{formatDuration(callDuration)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsActiveMinimized(true)}
                  className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                  title="Minimize"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5">
                {/* Phone number */}
                <p className="text-center text-lg font-mono font-bold text-slate-800">
                  {formatPhone(callerInfo?.phone)}
                </p>
                {callerInfo?.company && (
                  <p className="text-center text-sm text-slate-500 mt-0.5">{callerInfo.company}</p>
                )}

                {/* Noise filter & recording indicators */}
                <div className="flex items-center justify-center gap-3 mt-3 mb-1">
                  {isRecording && (
                    <span className="inline-flex items-center gap-1.5 text-red-500 text-xs">
                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      Recording
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-slate-100 my-4" />

                {/* Controls */}
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      onClick={toggleMute}
                      variant="outline"
                      size="icon"
                      className={`h-12 w-12 rounded-full border-2 transition-all ${isMuted ? 'bg-red-50 text-red-600 border-red-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                      title={isMuted ? 'Unmute' : 'Mute'}
                      data-testid="mute-btn"
                    >
                      {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </Button>
                    <span className="text-[10px] text-slate-400">{isMuted ? 'Unmute' : 'Mute'}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      onClick={toggleDeafen}
                      variant="outline"
                      size="icon"
                      className={`h-12 w-12 rounded-full border-2 transition-all ${isDeafened ? 'bg-amber-50 text-amber-600 border-amber-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                      title={isDeafened ? 'Undeafen' : 'Deafen'}
                      data-testid="deafen-btn"
                    >
                      {isDeafened ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </Button>
                    <span className="text-[10px] text-slate-400">{isDeafened ? 'Unmute' : 'Speaker'}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      onClick={toggleHold}
                      variant="outline"
                      size="icon"
                      className={`h-12 w-12 rounded-full border-2 transition-all ${isOnHold ? 'bg-yellow-50 text-yellow-700 border-yellow-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                      title={isOnHold ? 'Resume call' : 'Hold call'}
                      data-testid="hold-btn"
                    >
                      {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                    </Button>
                    <span className="text-[10px] text-slate-400">{isOnHold ? 'Resume' : 'Hold'}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      onClick={hangUp}
                      size="icon"
                      className="h-12 w-12 rounded-full bg-red-500 hover:bg-red-600 text-white border-2 border-red-500 hover:border-red-600"
                      title="End Call"
                      data-testid="hangup-btn"
                    >
                      <PhoneOff className="h-5 w-5" />
                    </Button>
                    <span className="text-[10px] text-red-400">End</span>
                  </div>
                </div>

                <DTMFKeypad call={activeCall} wrapperClass="mt-1" />
              </div>
            </div>
          </div>
        </>
      )}

    </>
  );
}
